import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, TrendingUp } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (n) => new Intl.NumberFormat("ar-SA", { maximumFractionDigits: 2 }).format(n || 0);

export default function Income() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("all");
  const [stats, setStats] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [revenues, setRevenues] = useState([]);

  const load = async () => {
    try {
      const statParams = campaignId !== "all" ? { campaign_id: campaignId } : {};
      const [c, s, b, e, r] = await Promise.all([
        api.get("/campaigns", { params: { portal: "commercial" } }),
        api.get("/stats/commercial", { params: statParams }),
        api.get("/beneficiaries", { params: { portal: "commercial", ...(campaignId !== "all" ? { campaign_id: campaignId } : {}) } }),
        api.get("/expenses", { params: campaignId !== "all" ? { campaign_id: campaignId } : {} }),
        api.get("/revenues", { params: campaignId !== "all" ? { campaign_id: campaignId } : {} }),
      ]);
      setCampaigns(c.data); setStats(s.data);
      setBeneficiaries(b.data); setExpenses(e.data); setRevenues(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        title="قائمة الدخل"
        subtitle="ملخص الإيرادات مقابل المصروفات — إجمالاً أو حسب حملة"
        actions={
          <Button
            data-testid="income-print-btn"
            variant="outline"
            onClick={() => {
              // Snapshot the currently-displayed data so print shows the exact same numbers
              const snapshot = { stats, beneficiaries, expenses, revenues, campaignId };
              sessionStorage.setItem("umrah_print_income", JSON.stringify(snapshot));
              window.open(`/print/commercial/income${campaignId !== "all" ? `?campaign=${campaignId}` : ""}`, "_blank");
            }}
            className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
          >
            <Printer className="h-4 w-4 ml-2" /> طباعة
          </Button>
        }
      />

      <Card className="p-4 mb-6 border-emerald-900/10 max-w-md">
        <Label className="text-sm text-slate-600 mb-1 block">اختر الحملة</Label>
        <Select value={campaignId} onValueChange={setCampaignId}>
          <SelectTrigger data-testid="income-campaign-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">إجمالي جميع الحملات</SelectItem>
            {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>#{c.number} {c.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </Card>

      <Card className="p-8 border-emerald-900/10">
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp className="h-6 w-6 text-emerald-800" />
          <h2 className="font-heading text-2xl font-bold text-emerald-950">قائمة الدخل</h2>
        </div>
        <div className="space-y-3 max-w-2xl" data-testid="income-summary">
          <Row label="إيرادات الرابط الذاتي (تسجيل عام)" value={fmt(stats?.public_beneficiary_revenue)} tone="emerald" />
          <Row label="إيرادات التسجيل اليدوي (الحضوري)" value={fmt(stats?.manual_beneficiary_revenue)} tone="emerald" />
          <Row label="إيرادات يدوية أخرى (تبرعات/رعاية)" value={fmt(stats?.manual_revenue)} tone="emerald" />
          <Row label="إجمالي الإيرادات" value={fmt(stats?.revenue)} tone="emerald-bold" />
          <Row label="عدد المستفيدين" value={fmt(stats?.beneficiaries_count)} tone="muted" />
          <div className="h-px bg-slate-200 my-4" />
          <Row label="إجمالي المصروفات" value={`(${fmt(stats?.expenses)})`} tone="gold" />
          <div className="h-px bg-slate-200 my-4" />
          <Row label="صافي الدخل" value={fmt(stats?.net_income)} tone={(stats?.net_income || 0) >= 0 ? "emerald-bold" : "danger"} />
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-6 mt-6">
        <Card className="p-6 border-emerald-900/10">
          <h3 className="font-heading font-bold text-emerald-950 mb-4">إيرادات المستفيدين</h3>
          <div className="text-sm text-slate-600">{beneficiaries.length} مستفيد</div>
          <div className="mt-3 max-h-64 overflow-y-auto text-sm space-y-1">
            {beneficiaries.slice(0, 50).map((b) => (
              <div key={b.id} className="flex justify-between p-2 bg-slate-50 rounded">
                <span className="text-emerald-950 font-medium">{b.name}</span>
                <span className="tabular-nums text-emerald-800 font-bold">{fmt(b.amount)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6 border-emerald-900/10">
          <h3 className="font-heading font-bold text-emerald-950 mb-4">الإيرادات اليدوية</h3>
          <div className="text-sm text-slate-600">{revenues.length} سجل</div>
          <div className="mt-3 max-h-64 overflow-y-auto text-sm space-y-1">
            {revenues.slice(0, 50).map((r) => (
              <div key={r.id} className="flex justify-between p-2 bg-slate-50 rounded">
                <span className="text-emerald-950">{r.date} · {r.category}</span>
                <span className="tabular-nums text-emerald-800 font-bold">{fmt(r.amount)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6 border-emerald-900/10">
          <h3 className="font-heading font-bold text-emerald-950 mb-4">تفصيل المصروفات</h3>
          <div className="text-sm text-slate-600">{expenses.length} سجل</div>
          <div className="mt-3 max-h-64 overflow-y-auto text-sm space-y-1">
            {expenses.slice(0, 50).map((e) => (
              <div key={e.id} className="flex justify-between p-2 bg-slate-50 rounded">
                <span className="text-emerald-950">{e.date} · {e.category}</span>
                <span className="tabular-nums text-amber-700 font-bold">{fmt(e.amount)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, tone }) {
  const toneMap = {
    emerald: "text-emerald-800",
    "emerald-bold": "text-emerald-800 text-2xl font-black",
    gold: "text-amber-700",
    danger: "text-red-600 text-2xl font-black",
    muted: "text-slate-500",
  };
  return (
    <div className="flex justify-between items-center py-2">
      <span className="text-slate-700 font-medium">{label}</span>
      <span className={`tabular-nums font-bold ${toneMap[tone]}`}>{value}</span>
    </div>
  );
}
